import { defineStore, acceptHMRUpdate } from 'pinia';
import clientConfig, { loadCompanyConfigFile } from '@/client_customs/config';
import defaultConfig from '@/js/defaultConfig';

function getCompanyConfigItem(user, configProp) {
  return user.company?.config?.[configProp];
}

const store = defineStore('users-accessRight', {
  state: () => ({
  }),

  getters: {
    config() {
      const { isBusiness, isEnterprise } = this;
      const companyConfig = this.companyConfig || {};
      const businessConfig = isBusiness && this.businessConfig || {};

      return {
        hasViews: !! (businessConfig.hasViews != undefined ? businessConfig.hasViews : false || companyConfig.views),
        views: companyConfig.views,
        hideExtenalLink: !! (businessConfig.hideExtenalLink || clientConfig.planningShare?.hideExtenalLink),
        meetingactions_extrafields: companyConfig.meetingactions_extrafields,
        projectsheet_steps_extrafields: companyConfig.projectsheet_steps_extrafields,
        links: _.extend({ hideOneDrive: false, hideGoogleDrive: false, hideDropbox: false, hideFileUpload: false }, businessConfig.links, clientConfig.links),
        availableIntegrations: businessConfig.availableIntegrations || clientConfig.integrations?.availableIntegrations || ['microsoft', 'google'],
        clientConfigTextUsers: clientConfig.textUsers,
        businessTextUsers: businessConfig.textUsers,
        virtualParticipantsIcons: { ...clientConfig.virtualParticipantsIcons, ...businessConfig.virtualParticipantsIcons },
        processSteps: defaultConfig.processSteps(), // mode kanban
        textUsers: companyConfig.textUsers?.map(item => ({ username: item }))
          || (clientConfig.textUsers || businessConfig.textUsers) && (businessConfig.textUsers || []).concat(clientConfig.textUsers || []).map(item => ({ username: item })),
        elHiddenContent: businessConfig.elHiddenContent || { task: null, milestone: ['checklist', 'progress', 'budgets'] },
        subprojectsVisibleDetails: businessConfig.subprojectsVisibleDetails || clientConfig.subprojects?.visibleDetails || ['progress', 'synthesis'],
        subprojectsSynthesisPopover: businessConfig.subprojectsSynthesisPopover || clientConfig.subprojects?.synthesisPopover || false,
        assignBubblesToCreator: isBusiness ? businessConfig.assignBubblesToCreator : false,
        assignActionsToCreator: isBusiness ? businessConfig.assignActionsToCreator : false,
        dependencyTypes: isBusiness ? !! businessConfig.dependencyTypes : false,
        budgetStates: Object.keys(businessConfig.budgetStates || {}).length ? businessConfig.budgetStates : defaultConfig.budgetStates(),
        workloadInHours: isBusiness ? businessConfig.workloadInHours : false,
        workloadHoursPerDay: isBusiness ? businessConfig.workloadHoursPerDay || 8 : null, // default = 8 hours.
        projectStatus: isBusiness ? clientConfig.projectStatus?.() || defaultConfig.projectStatus() : null,
        defaultProjectPrivacy: businessConfig.defaultProjectPrivacy || clientConfig.projects?.defaultPrivacy || 'company',
        timelineFormats: defaultConfig.timelineFormats(),
        businessHours: businessConfig.businessHours ? { start: 7 * 60, end: 19 * 60, ...businessConfig.businessHours } : null,
        businessHoursRatio: businessConfig.businessHours ? 24 * 60 / (businessConfig.businessHours.end - businessConfig.businessHours.start) : 1,
        designcolor: clientConfig.designcolor || { main: '#0169C1', second: '#777777' },
        themePalette: businessConfig.themePalette || '',
        icons: businessConfig.icons || clientConfig.icons,
        colors: businessConfig.colors || clientConfig.colors,
        automations: isEnterprise && businessConfig.automations || businessConfig.automations?.filter(automation => typeof automation == 'string') || null, // enterprise || custom automations only
        // functions
        getCategoryColor(category) {
          if (! category) return 'fff';
          return businessConfig.categoryColors?.[category] || clientConfig.categoryColors?.[category] || window.str2color(category);
        },
        elTitleTooltip: clientConfig.elements?.elTitleTooltip || companyConfig.elTitleTooltipField && (el => el.data[companyConfig.elTitleTooltipField]) || null,
      };
    },
  },

  actions: {
    set(values) {
      Object.keys(values).forEach((prop) => {
        this[prop] = values[prop];
      });
    },

    async load(user) {
      const isTrial = user.company?.isTrial && user.company.premiumExpireDate;
      const isPremium = user.company?.isPremium;
      const isBusiness = isPremium && user.company.isBusiness;
      const isEnterprise = isPremium && getCompanyConfigItem(user, 'isEnterprise');
      const configFile = getCompanyConfigItem(user, 'configFile') || ''; // company config custom file
      if (configFile) await loadCompanyConfigFile(configFile);

      this.set({
        companyId: user.company_id,
        isTrial,
        isPremium,
        isBusiness,
        isEnterprise,
        clientConfig,
        companyConfig: user.company?.config || {},
        businessConfig: user.company?.business_config || {},
        maintenanceMode: getCompanyConfigItem(user, 'maintenanceMode'),
        isAdmin: ['superadmin', 'admin', 'company_admin'].indexOf(user.access_right) > -1,
        isConsultOnly: user.access_right == 'consult',
        maxprojects: Math.max((isPremium ? user.company.maxprojects : user.maxprojects) || 0, 1),
        hasMonitoringPlan: isEnterprise || getCompanyConfigItem(user, 'monitoringplan'),
        hasOrganization: user.hasOrganization,
        hasGroups: isBusiness || getCompanyConfigItem(user, 'hasGroups'),
        canAssignGroups: isBusiness || getCompanyConfigItem(user, 'canAssignGroups'),
        hasUserWorkloadIndicator: isBusiness || getCompanyConfigItem(user, 'hasUserWorkloadIndicator'),
        hasMacroTasks: isBusiness || getCompanyConfigItem(user, 'hasMacroTasks'),
        hasWorkingViews: isBusiness || getCompanyConfigItem(user, 'hasWorkingViews'),
        hasProcessDashboard: isBusiness || getCompanyConfigItem(user, 'hasProcessDashboard'),
        hasImportTemplate: isBusiness || getCompanyConfigItem(user, 'hasImportTemplate'),
        hideBubbleMeeting: getCompanyConfigItem(user, 'bubblemeeting') === false,
        hasMeeting: user.company?.hasMeeting || false,
        hasHerblayTicketing: getCompanyConfigItem(user, 'herblayTicketing'), /* Herblay */
        hasLocabriImport: getCompanyConfigItem(user, 'locabriImport') && user.access_right != 'consult', /* Locabri */
        canCreateMeeting: (() => { // boolean
          if (isPremium && user.meeting_right == 'organizer') return true; // isOrganizer
          const trialEnd = getCompanyConfigItem(user, 'meeting_trial_end');
          return trialEnd && moment().isBefore(trialEnd);
        })(),
      });
    },

    updateBusinessConfig(values) {
      if (this.isBusiness) {
        Object.keys(values).forEach((prop) => {
          this.businessConfig[prop] = values[prop];
        });
      }
      return window.apiSrv.call(`companies/${this.companyId}`, 'update', { business_config: this.businessConfig });
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
